import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useCheckboxGroupOptions } from '@alltrails/denali/components/CheckboxGroup';
import { CheckboxFilterConfig } from '@alltrails/denali/components/FilterBar';
import TrailEngagement from '../../types/TrailEngagement';

const values: TrailEngagement[] = ['saved', 'not_saved', 'completed', 'not_completed'];
const labels = defineMessages<TrailEngagement>({
  saved: {
    defaultMessage: 'Saved'
  },
  not_saved: {
    defaultMessage: 'Not saved'
  },
  completed: {
    defaultMessage: 'Completed'
  },
  not_completed: {
    defaultMessage: 'Not completed'
  }
});

export const getValidTrailEngagements = (completions: string[]) =>
  completions.filter(engagement => values.includes(engagement as TrailEngagement)) as TrailEngagement[];

const useTrailEngagementFilterConfig = (
  trailEngagement: TrailEngagement[] = [],
  onTrailEngagementChange: (trailEngagement: TrailEngagement[]) => void
): CheckboxFilterConfig => {
  const intl = useIntl();

  const { onChange, options, clearOptions, selectedOptions, setOptions } = useCheckboxGroupOptions(
    values.map(value => ({
      id: value,
      selected: trailEngagement.includes(value),
      testId: value,
      text: intl.formatMessage(labels[value])
    }))
  );

  useEffect(() => {
    const areEqual =
      selectedOptions.length === trailEngagement.length && selectedOptions.every(option => trailEngagement.includes(option.id as TrailEngagement));
    if (!areEqual) {
      setOptions(
        values.map(value => ({
          id: value,
          selected: trailEngagement.includes(value),
          testId: value,
          text: intl.formatMessage(labels[value])
        }))
      );
    }
    // Only want to update when we get new values.
    // stringify the array since the object itself is constantly changing.
  }, [JSON.stringify(trailEngagement)]);

  useEffect(() => {
    onTrailEngagementChange(selectedOptions.map(option => option.id as TrailEngagement));
    // Only want to trigger this the selected value changes
  }, [selectedOptions]);

  return {
    type: 'checkbox',
    id: 'trail-engagement',
    filterChip: {
      onClear: clearOptions,
      selectedItems: selectedOptions?.map(option => option.text),
      testId: 'trail-engagement-filter-chip',
      unselectedText: intl.formatMessage({ defaultMessage: 'Saved & completed' })
    },
    checkboxGroup: {
      labelText: intl.formatMessage({ defaultMessage: 'Saved & completed' }),
      onChange,
      options,
      testId: 'trail-engagement-filter'
    }
  };
};

export default useTrailEngagementFilterConfig;
